export const CHECKOUT_URL = 'https://otclinicaltoolkit.impultienda.ar/checkout'

export const IMAGES = {
  bundle:
    'https://cdn.magicpatterns.com/uploads/f5kfyrfBMMqx9KG6Kxp4Uu/ChatGPT_Image_3_sept_2026,_19_17_34.png',
  pediatric:
    'https://cdn.magicpatterns.com/patterns/generated-images/8ac6b74b-37d5-4a85-83ce-cb9763b69c74.jpg',
  adult:
    'https://cdn.magicpatterns.com/patterns/generated-images/f0306e5f-29ad-4d14-a661-1c5cda8d149c.jpg',
  desk: 'https://cdn.magicpatterns.com/patterns/generated-images/b4197a7a-f8fb-4442-8df5-df2e724f72a7.jpg',
}

export type Stage = {
  label: string
  description: string
}

export const stages: Stage[] = [
  {
    label: 'Assess',
    description: 'Observe performance and identify functional barriers.',
  },
  {
    label: 'Plan',
    description:
      'Turn findings into priorities and a clear intervention direction.',
  },
  {
    label: 'Treat',
    description:
      'Select and adapt purposeful interventions for the client and context.',
  },
  {
    label: 'Track',
    description:
      'Document performance, monitor change and organize what comes next.',
  },
]

export type VolumeIcon =
  | 'assess'
  | 'plan'
  | 'treat'
  | 'pediatric'
  | 'adult'
  | 'track'

export type Volume = {
  number: number
  stage: string
  title: string
  benefit: string
  icon: VolumeIcon
  items: string[]
}

export const volumes: Volume[] = [
  {
    number: 1,
    stage: 'Assess',
    title: 'The Clinical Observation & Assessment System',
    benefit: 'Turn scattered observations into clear clinical priorities.',
    icon: 'assess',
    items: [
      'Occupational Profile Builder',
      'ADL & IADL Observation Tools',
      'Environmental Performance Scans',
      'Functional Barrier Mapping',
    ],
  },
  {
    number: 2,
    stage: 'Plan',
    title: 'Clinical Reasoning & Intervention Designer',
    benefit:
      'Turn assessment findings into structured intervention direction.',
    icon: 'plan',
    items: [
      'Goal Architecture Tools',
      'Clinical Reasoning Frameworks',
      'Session Structuring System',
      'Grading & Scaffolding Tools',
    ],
  },
  {
    number: 3,
    stage: 'Treat',
    title: 'Therapeutic Activity & Intervention Library',
    benefit:
      'Ready-to-use intervention ideas organized by functional objective.',
    icon: 'treat',
    items: [
      'Fine & Gross Motor Activities',
      'Visual-Motor & Praxis Activities',
      'Executive Function Strategies',
      'ADL & Participation Interventions',
    ],
  },
  {
    number: 4,
    stage: 'Pediatric',
    title: 'Pediatric Practice Companion',
    benefit: 'Practical resources for everyday pediatric OT practice.',
    icon: 'pediatric',
    items: [
      'Play-Based Intervention',
      'School Participation',
      'Pediatric Self-Care',
      'Caregiver Collaboration',
    ],
  },
  {
    number: 5,
    stage: 'Adult',
    title: 'Adult Rehab & Functional Independence',
    benefit:
      'Functional resources for adult rehabilitation and participation.',
    icon: 'adult',
    items: [
      'ADL & IADL Rehabilitation',
      'Functional Mobility & Safety',
      'Upper Extremity Function',
      'Energy Conservation & Adaptation',
    ],
  },
  {
    number: 6,
    stage: 'Track',
    title: 'The Documentation & Progress System',
    benefit: 'Keep documentation, reassessment and progress organized.',
    icon: 'track',
    items: [
      'Progress Note Templates',
      'Outcome Tracking',
      'Home Program Documentation',
      'Discharge Planning Tools',
    ],
  },
]

export type Bonus = {
  number: number
  title: string
  description: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'Clinical Quick Reference Cards',
    description:
      'Fast-reference clinical prompts for the ASSESS → PLAN → TREAT → TRACK workflow.',
  },
  {
    number: 2,
    title: 'Intervention Planning Sheets',
    description:
      'Structured pages for organizing session focus, activity selection and adaptation options.',
  },
  {
    number: 3,
    title: 'Progress Tracker',
    description:
      'A simple framework for organizing functional observations and changes over time.',
  },
  {
    number: 4,
    title: 'Activity Modifications Guide',
    description:
      'Practical grading and adaptation ideas for adjusting activities to client needs and context.',
  },
  {
    number: 5,
    title: 'Goal Setting Templates',
    description:
      'Frameworks for turning findings into clear, functional, occupation-focused goals.',
  },
]

export const builtToHelp: string[] = [
  'Spend less time preparing sessions from a blank page',
  'Keep clinical resources organized in one system',
  'Move from observation to intervention with a clearer structure',
  'Track functional progress more consistently',
]

export const offerIncludes: string[] = [
  '6 Professional Clinical Volumes',
  '5 Practical Bonus Resources',
  'ASSESS → PLAN → TREAT → TRACK framework',
  'Instant Digital Access',
  'One-Time Payment',
]

export type Faq = {
  question: string
  answer: string
}

export const faqs: Faq[] = [
  {
    question: 'Is OT Clinical Toolkit™ a physical product?',
    answer:
      'No. OT Clinical Toolkit™ is a 100% digital professional resource. Nothing is shipped physically.',
  },
  {
    question: 'Is this a course?',
    answer:
      'No. This is not a course or training program. It is a practical digital clinical resource system designed for reference and day-to-day organization.',
  },
  {
    question: 'Is this software or an app?',
    answer:
      'No. There is nothing to install and no subscription. The resources are delivered digitally for you to access and use as professional references.',
  },
  {
    question: 'Is it suitable for newer Occupational Therapists?',
    answer:
      'Yes. The structured framework can be especially useful when you want a clearer starting point for organizing assessment, planning, intervention and documentation.',
  },
  {
    question: 'Can experienced OTs use it?',
    answer:
      'Yes. It is also designed for experienced Occupational Therapists who want to reduce scattered resources and keep frequently used clinical references in a more organized system.',
  },
  {
    question: 'Does it replace clinical judgment?',
    answer:
      'No. OT Clinical Toolkit™ is a professional reference and organization resource. It does not replace individualized assessment, clinical reasoning, professional judgment or applicable standards of practice.',
  },
  {
    question: 'How do I receive it?',
    answer: 'You receive digital access after completing your purchase.',
  },
]
